import React, { useEffect, useState } from "react";
import { getCachedPdfDocument } from "./pdfCache"; // 여기로 임포트
import LoadingDots from "../../../../component/common/LoadingDots";

/**
 * PDF 한 페이지를 캔버스로 렌더해 이미지로 보여준다.
 *
 * <p><b>[주의] 이 프로젝트에는 Tailwind 가 없다.</b> 예전엔 호출부가
 * {@code className="w-full h-full object-cover"} 를 넘겼는데, 그 클래스들이 정의된 적이 없어
 * 전부 무시됐다. 그래서 img 가 <b>원본 렌더 크기 그대로</b> 그려지고 부모의 overflow:hidden 에
 * 잘려, 슬라이드 왼쪽 위 귀퉁이만 확대된 것처럼 보였다(글자가 화면 밖으로 넘어감).
 * 크기·맞춤은 클래스 대신 <b>인라인 스타일</b>로 직접 준다.
 */
export default function PDFSingleThumbnail({
                                               pdfUrl,
                                               pageNumber = 1,
                                               scale = 0.3,
                                               className = "",
                                               /** 상자를 채우는 방식 — 기본은 잘라내지 않는 contain. */
                                               objectFit = "contain",
                                           }) {
    const [thumbnailSrc, setThumbnailSrc] = useState(null);

    useEffect(() => {
        let isMounted = true;

        async function renderPage() {
            try {
                // ★ 캐시 활용
                const pdf = await getCachedPdfDocument(pdfUrl);
                // 만약 pageNumber가 범위 밖이면 1페이지로
                const validPageNumber = Math.max(1, Math.min(pageNumber, pdf.numPages));
                const page = await pdf.getPage(validPageNumber);

                const viewport = page.getViewport({ scale });
                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                const renderContext = { canvasContext: context, viewport };
                await page.render(renderContext).promise;

                const dataUrl = canvas.toDataURL("image/png");
                if (isMounted) setThumbnailSrc(dataUrl);
            } catch (error) {
                console.error("PDF 페이지 썸네일 생성 오류:", error);
            }
        }

        renderPage();
        return () => {
            isMounted = false;
        };
    }, [pdfUrl, pageNumber, scale]);

    // 렌더 전 — 공통 로딩 표시를 썸네일 상자 <b>정중앙</b>에 놓는다.
    // 상자가 이미 비율로 높이를 잡고 있으므로, 여기서 높이를 만들지 말고(py:0) 꽉 채워 가운데 정렬만 한다.
    // 그래야 로딩 → 이미지로 바뀔 때 카드 높이가 흔들리지 않는다.
    if (!thumbnailSrc) {
        return (
            <div
                className={className}
                style={{
                    width: "100%", height: "100%",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}
            >
                <LoadingDots compact label="미리보기 준비 중" sx={{ py: 0 }} />
            </div>
        );
    }

    return (
        <img
            src={thumbnailSrc}
            alt={`pdf-page-${pageNumber}`}
            className={className}
            style={{ width: "100%", height: "100%", objectFit, display: "block" }}
        />
    );
}